import { useCallback, useState } from 'react';

/**
 * 다음(카카오) 우편번호 찾기.
 *
 * 원본: randombox_order_detail.php 가 `daum.Postcode` 를 그대로 썼다 (인증키
 * 없이 쓰는 공개 위젯). 여기서도 같은 스크립트를 그대로 쓴다 - 서버를 거치지
 * 않고 브라우저에서 바로 뜨는 팝업이라, 다시 구현하는 대신 스크립트를 늦게
 * (이 화면에 들어올 때만) 불러와 그대로 연다.
 */

export interface DaumAddress {
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
  /** 아래는 위젯이 늘 주지만 주문 수정 화면은 안 쓰던 칸 - 지역별배송비 테스트 주소가 쓴다. */
  address?: string;
  autoJibunAddress?: string;
  sido?: string;
  sigungu?: string;
  buildingName?: string;
}

declare global {
  interface Window {
    daum?: {
      Postcode: new (options: { oncomplete: (data: DaumAddress) => void }) => { open: () => void };
    };
  }
}

const SCRIPT_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

let scriptPromise: Promise<void> | null = null;

function loadPostcodeScript(): Promise<void> {
  if (window.daum?.Postcode) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null; // 다음 클릭 때 다시 시도할 수 있게 한다.
        reject(new Error('주소 검색 스크립트를 불러오지 못했습니다.'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export function useAddressSearch() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((onComplete: (address: DaumAddress) => void) => {
    setError(null);
    setLoading(true);
    loadPostcodeScript()
      .then(() => {
        setLoading(false);
        new window.daum!.Postcode({ oncomplete: onComplete }).open();
      })
      .catch((err: Error) => {
        setLoading(false);
        setError(err.message);
      });
  }, []);

  return { open, loading, error };
}
